import * as React from "react"
import { Plus, Filter } from "lucide-react"
import { ExpenseCard } from "./expense-card"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { ExpenseListSkeleton } from "@/components/ui/loading-state"
import { groupBy } from "@/lib/utils"
import type { Expense } from "@/types/domain.types"

interface ExpenseListProps {
  expenses: Expense[]
  isLoading?: boolean
  isError?: boolean
  onAddExpense?: () => void
  onEditExpense?: (expense: Expense) => void
  onDeleteExpense?: (expenseId: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  groupBy?: 'date' | 'category' | 'none'
  showFilters?: boolean
}

export const ExpenseList: React.FC<ExpenseListProps> = ({
  expenses,
  isLoading,
  isError,
  onAddExpense,
  onEditExpense,
  onDeleteExpense,
  onLoadMore,
  hasMore,
  isLoadingMore,
  groupBy: groupByOption = 'date',
  showFilters = true,
}) => {

  const loadMoreRef = React.useRef<HTMLDivElement>(null)

  // Set up intersection observer for infinite scroll
  React.useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore()
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [onLoadMore, hasMore, isLoadingMore])

  if (isLoading) {
    return <ExpenseListSkeleton />
  }

  if (isError) {
    return (
      <EmptyState
        title="Failed to load expenses"
        description="Something went wrong while loading your expenses. Please try again."
        action={{
          label: "Retry",
          onClick: () => window.location.reload(),
        }}
      />
    )
  }

  if (expenses.length === 0) {
    return (
      <EmptyState
        icon={<Plus className="h-6 w-6" />}
        title="No expenses yet"
        description="Start tracking your group expenses by adding your first expense."
        action={
          onAddExpense
            ? {
                label: "Add Expense",
                onClick: onAddExpense,
              }
            : undefined
        }
      />
    )
  }

  const renderExpenseGroups = () => {
    // First, sort all expenses by date (newest first) to ensure consistent ordering
    const sortedExpenses = [...expenses].sort((a, b) => {
      const dateA = new Date(a.date || a.created_at || 0)
      const dateB = new Date(b.date || b.created_at || 0)
      return dateB.getTime() - dateA.getTime()
    })

    if (groupByOption === 'none') {
      return (
        <div className="space-y-3">
          {sortedExpenses.map((expense) => (
            <ExpenseCard
              key={expense.id}
              expense={expense}
              onEdit={onEditExpense}
              onDelete={onDeleteExpense}
            />
          ))}
        </div>
      )
    }

    // Group the already-sorted expenses
    const groups = groupByOption === 'date'
      ? groupBy(sortedExpenses, (expense) => {
          const dateField = expense.date || expense.created_at
          if (!dateField) return 'Unknown Date'
          const date = new Date(dateField)
          return isNaN(date.getTime()) ? 'Unknown Date' : date.toDateString()
        })
      : groupBy(sortedExpenses, (expense) => expense.category?.name || 'Uncategorized')

    // Sort group keys (dates or categories)
    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
      if (groupByOption === 'date') {
        // For date groups, parse and compare dates (newest first)
        if (a === 'Unknown Date') return 1
        if (b === 'Unknown Date') return -1
        const dateA = new Date(a)
        const dateB = new Date(b)
        return dateB.getTime() - dateA.getTime()
      }
      // For category groups, sort alphabetically
      return a.localeCompare(b)
    })

    return (
      <div className="space-y-6">
        {sortedGroupKeys.map((groupKey) => {
          // Expenses within each group maintain their sorted order from above
          const groupExpenses = groups[groupKey]

          return (
            <div key={groupKey} className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                {groupByOption === 'date'
                  ? formatGroupDate(groupKey)
                  : groupKey
                }
              </h3>
              <div className="space-y-3">
                {groupExpenses.map((expense) => (
                  <ExpenseCard
                    key={expense.id}
                    expense={expense}
                    onEdit={onEditExpense}
                    onDelete={onDeleteExpense}
                    showCategory={groupByOption !== 'category'}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {showFilters && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Filter className="mr-2 h-4 w-4" />
              Filter
            </Button>
          </div>
        </div>
      )}

      {renderExpenseGroups()}

      {/* Infinite scroll trigger */}
      {hasMore && (
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isLoadingMore ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Loading more expenses...
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={onLoadMore}
              className="text-muted-foreground"
            >
              Load more
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function formatGroupDate(dateString: string): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  } else {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }
}